import type { IntlShape } from "react-intl"

import type {
  RefLabelGroupJsonldReadCollectionRefLabelGroup,
  RefLabelGroupJsonldReadItemRefLabelGroupItem,
  RefLabelJsonldReadCollectionRefLabel,
  RefLabelJsonldReadItemAgencyItem,
  RefLabelJsonldReadItemClusterItem,
  RefLabelJsonldReadItemCollaboratorItem,
  RefLabelJsonldReadItemProperty,
  RefLabelJsonldReadItemRefLabelItem,
} from "@/networking/api"

import { isBlank, isNullOrUndefined } from "@/utils/ts-utils"
import type { Prettify } from "@/utils/types/utils"

export const REQUIRED_LOCALES = ["en_GB", "fr_FR"]
export const DEFAULT_LOCALE = "en_GB" as const
export const APP_LANGUAGES = ["en", "fr"] as const
export type APP_LANGUAGE = (typeof APP_LANGUAGES)[number]

export type Translation = { locale?: string; label?: string; field?: string }
export const is_translation = (obj: unknown | Translation): obj is Translation => {
  return obj instanceof Object && "label" in obj && "locale" in obj
}
type TranslatableBase = { code?: string; label?: string; translations?: Translation[] }
export type Translatable = TranslatableBase | Readonly<TranslatableBase>
export const is_translatable = (obj: unknown | Translation): obj is Translation => {
  return obj instanceof Object && "label" in obj && "translations" in obj
}

export type LocalRefLabel = Prettify<
  // | RefLabelJsonld
  | RefLabelJsonldReadItemRefLabelItem
  | RefLabelJsonldReadCollectionRefLabel
  | RefLabelJsonldReadItemProperty
  | RefLabelJsonldReadItemCollaboratorItem
  | RefLabelJsonldReadItemAgencyItem
  | RefLabelJsonldReadItemClusterItem
>
export const is_reflabel = (obj: unknown | LocalRefLabel): obj is LocalRefLabel => {
  return obj instanceof Object && "code" in obj && "label" in obj && "translations" in obj
}

export type LocalRefGroup = Prettify<
  // | RefLabelGroupJsonld
  | RefLabelGroupJsonldReadItemRefLabelGroupItem
  | RefLabelGroupJsonldReadCollectionRefLabelGroup
  | null
>

export const get_t11n = <T extends Translation>(
  translations?: T[],
  locale?: string,
  defaultLocale?: string,
  defaultText?: string
): string => {
  const dft = defaultText ?? ""
  if (isNullOrUndefined(translations) || translations.length === 0) return dft

  let locale_text = ""
  let dft_locale_text = ""

  translations.map(t => {
    if (t.locale?.split("_").shift() === locale) locale_text = t.label ?? ""
    if (t.locale?.split("_").shift() === defaultLocale) dft_locale_text = t.label ?? ""
  })

  return isBlank(locale_text) ? (isBlank(dft_locale_text) ? dft : dft_locale_text) : locale_text
}

export const get_t11n_label = (
  label?: LocalRefLabel | Translatable | null,
  intl?: IntlShape
): string => {
  if (Object.keys(intl?.messages ?? {}).includes(`labels.${label?.code ?? ""}`))
    return intl?.formatMessage({ id: `labels.${label?.code ?? ""}` }) ?? ""

  return get_t11n(
    label?.translations,
    intl?.locale,
    intl?.defaultLocale,
    label?.label ?? label?.code
  )
}

export const get_t11n_group = (label?: LocalRefGroup | null, intl?: IntlShape): string => {
  if (Object.keys(intl?.messages ?? {}).includes(`labels.${label?.group ?? ""}`))
    return intl?.formatMessage({ id: `labels.${label?.group ?? ""}` }) ?? ""

  return get_t11n(
    label?.translations,
    intl?.locale,
    intl?.defaultLocale,
    label?.label ?? label?.group
  )
}

export const mandatoryTranslations = ["fr_FR", "en_GB"] as const
export type MandatoryTranslations = {
  [k in (typeof mandatoryTranslations)[number]]: string
}
export type Translations = MandatoryTranslations & { [locale: string]: string }
